import React, { useMemo, useState } from "react";
import api from "../lib/api";
import { Search, Loader2, ChevronUp, ChevronDown, ChevronsUpDown, Download } from "lucide-react";
import { toast } from "sonner";
import { formatCpc, currencySymbolForLocation } from "../lib/currency";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../lib/csv";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

// Sort accessors. CPC + competition can be null (DFS sometimes omits them);
// those rows always sort to the bottom regardless of direction.
const SORT_ACCESSORS = {
  search_volume: (it) => it.keyword_info?.search_volume ?? null,
  cpc: (it) => it.keyword_info?.cpc ?? null,
  competition: (it) => it.keyword_info?.competition ?? null,
};

function SortIcon({ active, dir }) {
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <Icon
      className={`h-3 w-3 transition-opacity ${
        active ? "opacity-100 text-[#2c7a7b]" : "opacity-40 group-hover/sort:opacity-80"
      }`}
      strokeWidth={2.5}
    />
  );
}

function SortHeader({ label, sortKey, currentKey, currentDir, onClick, title }) {
  const active = currentKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      title={title || `Sort by ${label}`}
      data-testid={`kr-sort-${sortKey}`}
      className={`inline-flex items-center gap-1 hover:text-[#1a202c] transition-colors group/sort ${
        active ? "text-[#1a202c]" : ""
      }`}
    >
      <span>{label}</span>
      <SortIcon active={active} dir={currentDir} />
    </button>
  );
}

export default function KeywordResearch() {
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("United Kingdom");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tri-state sort: null → asc → desc → null.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null);

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      // Numeric columns default to desc — most useful "biggest first" view
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortKey || !sortDir) return items;
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return items;
    const decorated = items.map((it, i) => ({ it, i, v: accessor(it) }));
    decorated.sort((a, b) => {
      const aNull = a.v == null;
      const bNull = b.v == null;
      if (aNull && bNull) return a.i - b.i;
      if (aNull) return 1;
      if (bNull) return -1;
      if (a.v === b.v) return a.i - b.i;
      return sortDir === "asc" ? a.v - b.v : b.v - a.v;
    });
    return decorated.map((d) => d.it);
  }, [items, sortKey, sortDir]);

  const exportCsv = () => {
    if (!sortedItems.length) return;
    const currency = currencySymbolForLocation(location).trim() || "$";
    const columns = [
      { header: "Keyword", accessor: (it) => it.keyword ?? "" },
      { header: "Search Volume", accessor: (it) => it.keyword_info?.search_volume ?? "" },
      { header: `CPC (${currency})`, accessor: (it) => it.keyword_info?.cpc ?? "" },
      { header: "Competition", accessor: (it) => it.keyword_info?.competition ?? "" },
      { header: "Competition Level", accessor: (it) => it.keyword_info?.competition_level ?? "" },
      { header: "Location", accessor: () => location },
    ];
    const csv = rowsToCsv(sortedItems, columns);
    const filename = `keyword-research-${slugForFilename(keyword)}-${todayStamp()}.csv`;
    downloadCsv(filename, csv);
    toast.success(`Exported ${sortedItems.length} keywords`);
  };

  const run = async (e) => {
    e?.preventDefault();
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/seo/keyword-research", {
        keyword: keyword.trim(),
        location_name: location,
        language_name: "English",
        limit: 100,
      });
      setItems(data.result?.items || []);
      // Reset sort on new search — keeps "fresh result" intuitive
      setSortKey(null);
      setSortDir(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 pt-2" data-testid="keyword-research-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#a0aec0] font-semibold">
          Pages / Tools
        </div>
        <h1 className="font-display text-3xl mt-1 text-[#1a202c]">Keyword research</h1>
        <p className="text-sm text-[#718096] mt-1.5">
          Discover related keywords with volume, CPC and difficulty via DataForSEO Labs.
        </p>
      </div>

      <form onSubmit={run} className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[280px] relative">
          <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="seed keyword, e.g. 'seo tools'"
            data-testid="kr-keyword-input"
            className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          data-testid="kr-location-input"
          className="w-52 bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        />
        <button
          type="submit"
          disabled={loading || !keyword}
          data-testid="kr-search-button"
          className="btn-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Search
        </button>
        {items.length > 0 && (
          <button
            type="button"
            onClick={exportCsv}
            data-testid="kr-export-csv"
            title="Download results as CSV"
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        )}
      </form>

      <div className="card-soft overflow-hidden">
        <div className="grid grid-cols-12 gap-x-4 px-5 py-3.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
          <div className="col-span-6">Keyword</div>
          <div className="col-span-2 flex justify-end">
            <SortHeader
              label="Search volume"
              sortKey="search_volume"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
            />
          </div>
          <div className="col-span-2 flex justify-end">
            <SortHeader
              label={`CPC (${currencySymbolForLocation(location).trim() || "$"})`}
              sortKey="cpc"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
            />
          </div>
          <div className="col-span-2 flex justify-end">
            <SortHeader
              label="Difficulty"
              sortKey="competition"
              currentKey={sortKey}
              currentDir={sortDir}
              onClick={handleSort}
              title="Sort by paid-competition difficulty (0 = low, 1 = high)"
            />
          </div>
        </div>
        {loading && <div className="p-8 text-center text-sm text-[#a0aec0]">Querying DataForSEO Labs…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center text-sm text-[#a0aec0]">
            Enter a keyword above to see related ideas.
          </div>
        )}
        <div className="divide-y divide-[#edf2f7] max-h-[640px] overflow-y-auto">
          {sortedItems.map((it, i) => {
            const info = it.keyword_info || {};
            return (
              <div key={i} data-testid={`kr-row-${i}`} className="grid grid-cols-12 gap-x-4 px-5 py-2.5 hover:bg-[#f7fafc] text-sm items-center">
                <div className="col-span-6 truncate text-[#1a202c]">{it.keyword}</div>
                <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">{fmt(info.search_volume)}</div>
                <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">
                  {formatCpc(info.cpc, location)}
                </div>
                <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">
                  {info.competition_level || (info.competition != null ? info.competition.toFixed(2) : "—")}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
